import {createStyles, MultiSelect, Select, Stack, Text, TextInput} from '@mantine/core';
import React, {useEffect, useMemo, useState} from 'react';

import {AllowedEventTrigger, EventTriggerConfig} from './RoutineTypes';

interface EventTriggerConfigFormProps {
  config: Partial<EventTriggerConfig>;
  onChange: (updates: Partial<EventTriggerConfig>) => void;
  allowedTriggers: Array<AllowedEventTrigger>;
}

const useStyles = createStyles((theme) => ({
  fieldRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    flexWrap: 'wrap',
  },
  fieldLabel: {
    fontSize: theme.fontSizes.sm,
    fontWeight: 500,
    flexShrink: 0,
  },
}));

/**
 * Build a display label for an (objectType, field) trigger.
 * e.g., "entry" + "Review Status" → "Entry review status"
 */
// TODO(nweeden): Have the backend provide a pre-formatted display label instead of building one client-side
function getTriggerLabel(objectType: string, fieldDisplayName: string): string {
  const typeLabel = objectType.replace(/([A-Z])/g, ' $1').trim();
  return `${typeLabel.charAt(0).toUpperCase()}${typeLabel.slice(1)} ${fieldDisplayName.toLowerCase()}`;
}

export default function EventTriggerConfigForm({
  config,
  onChange,
  allowedTriggers,
}: EventTriggerConfigFormProps): JSX.Element {
  const {classes} = useStyles();

  // Flatten triggers into selectable options: one per (objectType, field) pair.
  // Keep metadata separate from the Select data items to avoid spreading custom
  // props onto DOM elements.
  const triggerOptions = useMemo(() => {
    const selectData: Array<{value: string; label: string}> = [];
    const metadata = new Map<string, {objectType: string; field: string; allowedValues: Array<string>}>();
    for (const trigger of allowedTriggers) {
      for (const f of trigger.fields) {
        const key = `${trigger.objectType}::${f.field}`;
        selectData.push({value: key, label: getTriggerLabel(trigger.objectType, f.displayName)});
        metadata.set(key, {
          objectType: trigger.objectType,
          field: f.field,
          allowedValues: f.allowedValues,
        });
      }
    }
    return {selectData, metadata};
  }, [allowedTriggers]);

  const selectedTriggerKey =
    config.object_type && config.field ? `${config.object_type}::${config.field}` : null;

  const selectedTrigger =
    selectedTriggerKey != null ? triggerOptions.metadata.get(selectedTriggerKey) : undefined;

  // Ensure the Select data always includes the current value so the label displays
  // even if allowedTriggers is still loading.
  const triggerSelectData = useMemo(() => {
    if (selectedTriggerKey == null || triggerOptions.selectData.some((d) => d.value === selectedTriggerKey)) {
      return triggerOptions.selectData;
    }
    return [{value: selectedTriggerKey, label: selectedTriggerKey}, ...triggerOptions.selectData];
  }, [triggerOptions.selectData, selectedTriggerKey]);

  const valueOptions = useMemo(
    () => (selectedTrigger?.allowedValues ?? []).map((v) => ({value: v, label: v})),
    [selectedTrigger]
  );

  // Ensure the MultiSelect data includes current to_values so they display during loading.
  const toValuesSelectData = useMemo(() => {
    const currentValues = config.to_values ?? [];
    const existingValues = new Set(valueOptions.map((v) => v.value));
    const missing = currentValues.filter((v) => !existingValues.has(v));
    if (missing.length === 0) {
      return valueOptions;
    }
    return [...valueOptions, ...missing.map((v) => ({value: v, label: v}))];
  }, [valueOptions, config.to_values]);

  const [objectIdsText, setObjectIdsText] = useState((config.object_ids ?? []).join(', '));

  useEffect(() => {
    setObjectIdsText((config.object_ids ?? []).join(', '));
  }, [config.object_ids]);

  return (
    <Stack spacing="md">
      <div className={classes.fieldRow}>
        <Text className={classes.fieldLabel}>Run routine after</Text>
        <Select
          placeholder="Select event"
          value={selectedTriggerKey}
          onChange={(val) => {
            const option = val != null ? triggerOptions.metadata.get(val) : undefined;
            if (option) {
              onChange({
                object_type: option.objectType,
                field: option.field,
                to_values: [],
                object_ids: config.object_ids ?? [],
              });
            }
          }}
          data={triggerSelectData}
          size="sm"
          sx={{flex: 1, minWidth: 160}}
        />
      </div>

      <div className={classes.fieldRow}>
        <Text className={classes.fieldLabel}>changes to</Text>
        <MultiSelect
          placeholder="Select values"
          value={config.to_values ?? []}
          onChange={(vals) => {
            onChange({to_values: vals});
          }}
          data={toValuesSelectData}
          size="sm"
          sx={{flex: 1, minWidth: 160}}
          disabled={selectedTrigger == null}
        />
        <Text className={classes.fieldLabel}>for</Text>
        {/* TODO(nweeden): Make object ID input more user-friendly (e.g. search/autocomplete) */}
        <TextInput
          placeholder="e.g. ent_xxx, ent_yyy"
          value={objectIdsText}
          onChange={(e) => {
            const text = e.currentTarget.value;
            setObjectIdsText(text);
            const ids = text
              .split(',')
              .map((s) => s.trim())
              .filter((s) => Boolean(s));
            onChange({object_ids: ids});
          }}
          size="sm"
          sx={{flex: 1, minWidth: 120}}
          disabled={selectedTrigger == null}
        />
      </div>
    </Stack>
  );
}
